"use client";

import { useEffect, useState } from "react";
import { useLottie } from "lottie-react";
import PropTypes from "prop-types";
import Skeleton from "../../molecules/skeletons/skeleton";
import CategoryImage from "../../molecules/categoryImage";

/**
 * A reusable Lottie animation component that fetches a Lottie JSON file
 * from the given path and renders it using `lottie-react`.
 *
 * @param {Object} props - Component props.
 * @param {string} props.animationPath - Path to the Lottie JSON file (should be in /public folder).
 * @param {boolean} [props.isLoop=true] - Whether the animation should loop.
 *
 * @returns {JSX.Element|null} The rendered Lottie animation or `null` while loading.
 */
const LottieComponent = ({
  placeholderImageUrl = "",
  animationPath,
  isLoop = true,
  width,
  height,
  hasPlaceHolder = false,
  classes = "",
  fallBackVariant = "image",
}) => {
  const [animationData, setAnimationData] = useState(null);

  /** */
  const returnFallBackElement = (variant) => {
    switch (variant) {
      case "skeleton":
        return <Skeleton customStyle={{ width: "100%", height: "100%" }} classes={classes} variant="rounded2xl" />;
      case "image":
        return (
          <CategoryImage
            imageSrc={placeholderImageUrl || "/icons/placeholder-bb.png"}
            alt="Lottie Animation Placeholder"
            width={width}
            height={height}
            sizes="12vw"
            classes="rounded-2xl"
            priority={true}
            borderRadius={16}
          />
        );
    }
  };

  useEffect(() => {
    if (animationPath) {
      fetch(animationPath)
        .then((res) => res.json())
        .then((data) => setAnimationData(data))
        .catch((err) => console.error("Failed to load Lottie JSON:", err));
    }
  }, [animationPath]);

  const options = {
    animationData,
    loop: isLoop,
  };

  const { View } = useLottie(options);

  if (hasPlaceHolder) {
    return animationData ? (
      <div className="w-full overflow-hidden rounded-16 object-cover">{View}</div>
    ) : (
      height > 0 && width > 0 && returnFallBackElement(fallBackVariant)
    );
  } else {
    return animationData && View;
  }
};
LottieComponent.propTypes = {
  animationPath: PropTypes.string.isRequired,
  isLoop: PropTypes.bool,
  hasPlaceHolder: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classes: PropTypes.string,
  placeholderImageUrl: PropTypes.string,
  fallBackVariant: PropTypes.string,
};
export default LottieComponent;
